type RelayEventStreamConstructor = {
  debug?: boolean
  eventSourceUrl: string
  onMessage: (event: MessageEvent) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Logger = (...args: Array<any>) => void

export class RelayEventStream {
  private eventSource: EventSource
  private eventSourceUrl: RelayEventStreamConstructor['eventSourceUrl']
  private keepAliveTime = 30_000
  private keepAliveTimer: NodeJS.Timeout
  private onMessage: RelayEventStreamConstructor['onMessage']
  private debugLog: Logger

  constructor({
    eventSourceUrl,
    onMessage,
    debug = false,
  }: RelayEventStreamConstructor) {
    this.debugLog = (...args) => {
      if (!debug) return
      console.log('[RelayEventStream]', ...args)
    }

    this.eventSourceUrl = eventSourceUrl
    this.eventSource = this.createEventSource()
    this.keepAliveTimer = this.createKeepAliveTimer()
    this.onMessage = onMessage

    this.eventSource.onmessage = this.createMessageHandler(
      onMessage,
      this.debugLog
    )
    this.eventSource.onopen = this.openHandler
    this.eventSource.onerror = this.errorHandler
  }

  createKeepAliveTimer() {
    return setTimeout(this.reconnectEventSource, this.keepAliveTime)
  }

  createEventSource() {
    this.debugLog(`Creating event source for ${this.eventSourceUrl}`)
    return new EventSource(this.eventSourceUrl)
  }

  openHandler = (event: Event) => {
    this.debugLog('EventSource opened', event)
  }

  errorHandler = (event: Event) => {
    console.error('EventSource failed to connect', event)
  }

  createMessageHandler = (
    onMessage: RelayEventStreamConstructor['onMessage'],
    debugLog: Logger
  ) => {
    return (event: MessageEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let parsedData: any = {}
      try {
        parsedData = JSON.parse(event.data)

        if (parsedData.event === 'ping') {
          debugLog('Keep-Alive event recieved, resetting Keep-Alive timer.')
          this.resetKeepAliveTimer()
          return
        }

        if (parsedData.event?.includes('connected')) {
          debugLog('Connection event recieved, doing nothing.')
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        console.error('[RelayEventStream] Unable to parse message.', e.message)
      }

      if ('data' in parsedData) {
        debugLog('Message recieved', event, parsedData.data)
        onMessage(parsedData.data)
      } else {
        console.error(
          '[RelayEventStream] Unknown message structure received.',
          parsedData
        )
      }
    }
  }

  resetKeepAliveTimer() {
    clearTimeout(this.keepAliveTimer)
    this.keepAliveTimer = this.createKeepAliveTimer()
  }

  reconnectEventSource = () => {
    this.debugLog('Keep-Alive timer expired, reconnecting EventSource')
    this.eventSource.close()
    this.eventSource = this.createEventSource()
    this.eventSource.onmessage = this.createMessageHandler(
      this.onMessage,
      this.debugLog
    )
    this.eventSource.onopen = this.openHandler
    this.eventSource.onerror = this.errorHandler
  }
}
